import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import Image from "shared/assets/index";
import { useCallback, useEffect, useRef, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { IPaymentByElectronicDocument } from "../Models/IPaymentInformation";
import {
  Spinner,
  getPaymentByElectronicDocument,
  downloadPaymentAttachments,
  Attachment,
} from "shared";
import styles from "../styles/tabPaymentInformation.module.scss";
import { ModalDocumentsPdf } from "shared/components/ModalDocuments.pdf";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const contentDataGlossas = [
  { label: "N° doc pago (TD)", idLabel: "paymentNumber" },
  { label: "Valor Pagado", idLabel: "paymentTotalValue" },
  { label: "N° CDP", idLabel: "cdpNumber" },
  { label: "N° RPC", idLabel: "rpcNumber" },
  { label: "N° Resolución", idLabel: "paymentResolution" },
  { label: "Rubro", idLabel: "paymentCategory" },
  { label: "Fecha de Pago", idLabel: "paymentAt" },
  { label: "PDF Del Documento de Pago (TD)", idLabel: "attachmentId" },
  {
    label: "PDF Del Documento de Resolución",
    idLabel: "resolutionAttachmentId",
  },
];

interface TabPaymentInformationProps {
  certificateNumber?: string;
}

export const TabPaymentInformation = ({
  certificateNumber = "",
}: TabPaymentInformationProps) => {
  const params = useParams();
  const idDocument = params.idDocument || "";
  const [loading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [attachmentsCache, setAttachmentsCache] = useState<Attachment[] | null>(
    null,
  );
  const [dataPayments, setDataPayments] = useState<{
    columns: { label: string; idLabel: string }[];
    content: { [key: string]: any }[];
    title: string;
  }>({
    columns: contentDataGlossas,
    content: [],
    title: "Información de pagos",
  });
  const [pdfLoading, setPdfLoading] = useState(false);

  const paymentRef = useRef<HTMLDivElement>(null);

  const scrollPayments = () => {
    paymentRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchAttachments = useCallback(async () => {
    if (attachmentsCache) {
      return attachmentsCache;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await downloadPaymentAttachments({
        electronicDocumentId: parseInt(idDocument),
      });
      setAttachmentsCache(response.data.attachments);
      return response.data.attachments;
    } catch (err) {
      console.error("Error fetching attachments:", err);
      setError(
        "Error al cargar los documentos adjuntos. Por favor intente nuevamente.",
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [idDocument, attachmentsCache]);

  const [documentPdf, setDocumentPdf] = useState<any>({});
  const [openIframePdf, setOpenIframePdf] = useState(false);

  const viewDocument = useCallback(
    async (attachmentId: number) => {
      setPdfLoading(true);
      setError(null);
      try {
        const attachments = await fetchAttachments();
        const attachment = attachments.find((att) => att.id === attachmentId);

        if (!attachment || !attachment.fileContentBase64) {
          setError("No se encontró el documento solicitado.");
          return;
        }

        setDocumentPdf(attachment.fileContentBase64);
        setOpenIframePdf(true);
      } catch (err) {
        console.error("Error viewing document:", err);
        setError(
          "Error al procesar el documento. El formato del archivo no es válido.",
        );
      } finally {
        setPdfLoading(false);
      }
    },
    [fetchAttachments],
  );

  const navigate = useNavigate();

  const handleViewPaymentDetails = useCallback(() => {
    if (!certificateNumber) {
      Swal.fire({
        title:
          "No se encontró el número de certificado para consultar el detalle del pago",
        icon: "error",
        timer: 5500,
      });
      return;
    }

    try {
      navigate(`/autorizaciones/paquetes-exportados/${certificateNumber}`);
    } catch (err) {
      console.error("Error navigating to payment details:", err);
      Swal.fire({
        title: "Ocurrió un error al intentar abrir el detalle del pago",
        icon: "error",
        timer: 5500,
      });
    }
  }, [certificateNumber, navigate]);

  const convertDataGlossas = useCallback(
    (payload: IPaymentByElectronicDocument) => {
      const data = {
        columns: contentDataGlossas,
        content: [
          {
            paymentTotalValue:
              payload.paymentTotalValue.toLocaleString("es-CO", {
                style: "currency",
                currency: "COP",
              }) || "-",
            cdpNumber: payload.cdpNumber || "-",
            rpcNumber: payload.rpcNumber || "-",
            paymentResolution: payload.paymentResolution || "-",
            paymentNumber: payload.treasurySequence || "-",
            paymentCategory: payload.paymentCategory || "-",
            paymentAt: payload.paymentAt || "-",
            attachmentId: payload.paymentAttachmentId ? (
              <IconButton
                onClick={() => viewDocument(payload.paymentAttachmentId)}
                data-testid="open-pdf-button"
                size="small"
                color="primary"
                className={styles.iconButton}
              >
                <VisibilityIcon className={styles.icon} />
              </IconButton>
            ) : (
              "-"
            ),
            resolutionAttachmentId: payload.resolutionAttachmentId ? (
              <IconButton
                onClick={() => viewDocument(payload.resolutionAttachmentId!)}
                data-testid="open-resolution-pdf-button"
                size="small"
                color="primary"
                className={styles.iconButton}
              >
                <VisibilityIcon className={styles.icon} />
              </IconButton>
            ) : (
              "-"
            ),
          },
        ],
        title: "Información de pagos",
      };
      setDataPayments(data);
    },
    [viewDocument],
  );

  useEffect(() => {
    const fetchPaymentData = async () => {
      setIsLoading(true);
      try {
        const response = await getPaymentByElectronicDocument(idDocument);
        convertDataGlossas(response.data);
      } catch (error) {
        console.error("Error fetching payment data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (idDocument) {
      fetchPaymentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idDocument]);

  useEffect(() => {
    if (dataPayments.content.length > 0) {
      scrollPayments();
    }
  }, [dataPayments]);

  return (
    <div className={styles.containerPaymentInformation} ref={paymentRef}>
      {dataPayments.content.length > 0 ? (
        dataPayments.content.map((paymentInfo: any) => (
          <div
            className={`${styles.containerBody}`}
            data-testid={"tabs-details"}
            key={paymentInfo.attachmentId}
          >
            <div
              className="containerTitleCardTabs"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <img src={Image.iconoResumen} alt="" />
                <Typography>{paymentInfo.numberDocument}</Typography>
              </div>
              <Button
                onClick={handleViewPaymentDetails}
                variant="text"
                color="primary"
                size="small"
                sx={{ fontSize: "0.8rem", textTransform: "none" }}
                data-testid="view-payment-details-button"
              >
                Más detalles del pago
              </Button>
            </div>
            <Grid container direction={"row"} className={styles.contentDetails}>
              {dataPayments?.columns.map((column: any, index: number) => {
                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    className={styles.field}
                    key={index}
                  >
                    <Typography className={styles.titleDetails}>
                      {column.label}:{" "}
                    </Typography>
                    <span
                      data-testid={`input_value_${index}`}
                      className={styles.inputDetails}
                    >
                      {paymentInfo[column.idLabel]}
                    </span>
                  </Grid>
                );
              })}
            </Grid>
          </div>
        ))
      ) : (
        <Box
          className={styles.gridGlosasResultsNotResults}
          data-testid="grid-glosas"
        >
          <Box
            className={styles.containerNotResults}
            data-testid="title-not-results"
          >
            Aun no se ha cargado la información correspondiente de esta sección
          </Box>
        </Box>
      )}
      {(pdfLoading || loading) && <Spinner />}
      {error && (
        <Box className={styles.gridGlosasResultsNotResults}>
          <Typography color="error" data-testid="error-message">
            {error}
          </Typography>
        </Box>
      )}
      {openIframePdf && (
        <ModalDocumentsPdf
          document={documentPdf}
          openIframePdf={openIframePdf}
          handleOpenIframePdf={() => setOpenIframePdf(false)}
        />
      )}
    </div>
  );
};
